<script>
    import { onMount, tick } from "svelte";
    import {
        Dialog,
        File,
        LanguageSelector,
        Section,
        Toast,
    } from "@/components";
    import { toastPopup } from "@utils/common";
    import { getBasicInfo } from "@/utils/user";
    import {
        deleteAccount,
        trackEvent,
        updateDocById,
    } from "@/services/firebase";

    let { embedded = false } = $props();

    let userProfile = $state({
        email: "",
        name: "",
        surname: "",
        phone: "",
        language: "",
        firebaseUser: "",
        image: "",
    });

    let isUploading = $state(false);

    let showConfirm = $state(false);

    let toast = $state({});
    const LANG = {
        USER_PROFILE: translate("USER_PROFILE", "Perfil de usuario"),
        EMAIL: translate("EMAIL", "Email"),
        NAME: translate("NAME", "Nombre"),
        SURNAME: translate("SURNAME", "Apellidos"),
        PHONE_NUMBER: translate("PHONE_NUMBER", "Teléfono"),
        LANGUAGE: translate("LANGUAGE", "Idioma"),

        SAVE_CHANGES: translate("SAVE_CHANGES", "Guardar cambios"),

        REMOVE_WARNING_ZONE: translate(
            "REMOVE_WARNING_ZONE",
            "Zona de seguridad",
        ),
        REMOVE_USER: translate("REMOVE_USER", "Eliminar cuenta"),
        CONFIRM_DELETE_ACCOUNT: translate(
            "CONFIRM_DELETE_ACCOUNT",
            "¿Seguro que quieres eliminar tu cuenta? Esta acción es irreversible.",
        ),

        LANGUAGE_UPDATED: translate(
            "LANGUAGE_UPDATED",
            "Idioma actualizado correctamente ✔",
        ),
        USER_PROFILE_UPDATED: translate(
            "USER_PROFILE_UPDATED",
            "Perfil actualizado correctamente ✔",
        ),
    };
    import { isIOS } from "@/utils/platform";
    import appStore from "@/utils/store";
    import { translate } from "@/utils/format";
    import { CalooAdminConfig } from "@/services/admin";
    import { goTo } from "@/utils/router";
    /**
     * @param {globalThis.File} file
     */
    export async function resizeAndUploadImage(file) {
        // console.log("Resizing and uploading image", file);
        if (!file) return;

        const imageBitmap = await createImageBitmap(file);

        const canvas = document.createElement("canvas");
        const maxHeight = isIOS() ? 780 : 800;
        const scale = maxHeight / imageBitmap.height;
        canvas.width = imageBitmap.width * scale;
        canvas.height = maxHeight;

        const ctx = canvas.getContext("2d");
        if (!ctx) return null;
        ctx.drawImage(imageBitmap, 0, 0, canvas.width, canvas.height);

        /** @type {Blob | null} */
        const blob = await new Promise((resolve) =>
            canvas.toBlob(resolve, "image/jpeg", 0.9),
        );
        if (!blob) return null;
        const arrayBuffer = await blob.arrayBuffer();
        const buffer = Array.from(new Uint8Array(arrayBuffer));

        const filePayload = {
            buffer,
            type: "image/jpeg",
            name: file.name || "photo.jpg",
        };
        try {
            const BASE_URL =
                CalooAdminConfig?.googleApi ||
                import.meta.env["VITE_MEEFOD_API_URL"];

            const response = await fetch(`${BASE_URL}/uploadfoto/logo`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `${import.meta.env["VITE_MEFOOD_API_TOKEN"]}`,
                },
                body: JSON.stringify({ file: filePayload }),
            });
            const result = await response.json();
            console.log("✅ Imagen subida correctamente:", result);
            trackEvent("change_image_profile");
            return result;
        } catch (err) {
            console.error("❌ Error al subir imagen:", err);
            return null;
        }
    }

    /**
     * @param {globalThis.File | "remove"} fileOrCommand
     */
    async function handleFileChange(fileOrCommand) {
        if (fileOrCommand === "remove") {
            userProfile.image = "";
            console.log("Image removed from user profile");
            return;
        }

        isUploading = true;
        const image = await resizeAndUploadImage(fileOrCommand);
        console.log("Resized image:", image);
        if (image) userProfile.image = image;
        isUploading = false;
        trackEvent("change_image_profile");
    }

    onMount(async () => {
        console.log("Loading user profile...");
        const user = await getBasicInfo();
        userProfile = { ...userProfile, ...user };
        console.log("User profile loaded:", JSON.stringify(userProfile));
        trackEvent("user_profile_loaded");
    });

    async function saveUser() {
        // console.log("Saving user profile", userProfile);
        toast = toastPopup("User profile updated successfully!", "success");
        appStore.set("user", { ...userProfile });

        await updateDocById("users", userProfile.firebaseUser, {
            ...userProfile,
        });
        trackEvent("user_profile_updated");
    }
    /**
     * @param {string} lang
     */
    async function onLanguageChange(lang) {
        //  console.log("Language changed to:", lang);
        userProfile.language = lang;
        await updateDocById("users", userProfile.firebaseUser, {
            language: lang,
        });
        toast = toastPopup(
            translate("LANGUAGE_UPDATED", "Language updated successfully!"),
            "success",
        );
        appStore.set("user", { ...appStore.get("user"), language: lang });
        trackEvent("change_language", { language: lang });
        window.location.reload();
    }

    function removeUser() {
        showConfirm = true;
    }
</script>

{#snippet section()}
    <File
        type="image"
        onUpdateImage={handleFileChange}
        imageProfile={userProfile.image ? userProfile.image : null}
        {isUploading}
    />
    <ul class="section__content">
        <li>
            <label for="email">{translate("EMAIL", "Email")}</label>
            <input
                type="email"
                inputmode="email"
                bind:value={userProfile.email}
                disabled
            />
        </li>

        <li>
            <label for="name">{translate("NAME", "Name")}</label>
            <input type="text" bind:value={userProfile.name} />
        </li>

        <li>
            <label for="surname">{translate("SURNAME", "Surname")}</label>
            <input type="text" bind:value={userProfile.surname} />
        </li>

        <li>
            <label for="phone"
                >{translate("PHONE_NUMBER", "Phone number")}</label
            >
            <input
                type="number"
                inputmode="numeric"
                bind:value={userProfile.phone}
            />
        </li>
        <li>
            <LanguageSelector
                label={translate("LANGUAGE", "Language")}
                {onLanguageChange}
            />
        </li>

        <li class="legalDocs">
            <p class="legalDocs__title">
                {translate("LEGAL_DOCS", "Documentos legales")}
            </p>
            <div class="legalDocs__links">
                <a
                    class="legalDocs__link"
                    href="/assets/doc/legal/general-terms.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {translate(
                        "LEGAL_DOCS_GENERAL_TERMS",
                        "Condiciones generales",
                    )}
                </a>
                <a
                    class="legalDocs__link"
                    href="/assets/doc/legal/particular-terms.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {translate(
                        "LEGAL_DOCS_PARTICULAR_TERMS",
                        "Condiciones particulares",
                    )}
                </a>
            </div>
        </li>

        <li class="--remove">
            <p>{translate("REMOVE_WARNING_ZONE", "Warning zone")}</p>
            <button class="--delete" onclick={() => removeUser()}>
                {translate("REMOVE_USER", "Remove user")}
            </button>
        </li>
    </ul>
    <Dialog
        isOpen={showConfirm}
        message={translate(
            "CONFIRM_DELETE_ACCOUNT",
            "Do you really want to delete this account?",
        )}
        labelOk={translate("DELETE", "Delete")}
        labelCancel={translate("CANCEL", "Cancel")}
        onOk={() => {
            showConfirm = false;
            deleteAccount()
                .then(() => {
                    toast = toastPopup(
                        translate("USER_DELETED", "User deleted successfully!"),
                        "success",
                    );
                    trackEvent("delete_user");
                    setTimeout(() => {
                        goTo("/logout");
                    }, 2000);
                })
                .catch((error) => {
                    console.error("Error deleting user:", error);
                    toast = toastPopup(
                        translate("ERROR_DELETING_USER", "Error deleting user"),
                        "error",
                    );
                    setTimeout(() => {
                        goTo("/logout");
                    }, 2000);
                });
        }}
        onCancel={() => (showConfirm = false)}
    />
{/snippet}

{#if embedded}
    <div class="userProfile userProfile--embedded">
        {@render section()}
        <div class="userProfile__actions">
            <button class="--primary" onclick={saveUser}>
                {translate("SAVE_CHANGES", "Guardar cambios")}
            </button>
        </div>
    </div>

    {#if toast}
        <Toast {...toast} />
    {/if}
{:else}
    <div class="userProfile">
        <Section
            headerText={translate("USER_PROFILE", "User Profile")}
            returnBtn={true}
            actionBack="dashboard"
            className="userProfile"
            textBtn="Guardar cambios"
            {section}
            {toast}
            onClickButton={saveUser}
        />
    </div>
{/if}

<style lang="scss">
    @use "./styles/index.scss";
</style>
